import React, { useState, useEffect, useRef } from "react";
import "./aboutUs.css"; // Assuming you have a CSS file for styling
import NavigationBar from "../NewLandingPage/NavigationBar";

function AboutUsPage() {
  const [expanded, setExpanded] = useState(null);
  const timelineRef = useRef(null);

  const cards = [
    {
      id: "why",
      title: "Why We Exist",
      short:
        "Most businesses operate reactively. We exist to flip that script—giving companies the power to predict and preempt.",
      full: `We started Radar+ because we believe that every business—no matter its size—deserves the power of foresight.
      In a world where strategic clarity is the rarest competitive advantage, we’ve made it accessible.
      Radar+ brings complete visibility across operations, finance, and market activity.
      But more than awareness, it automates execution. We exist to ensure no company is caught off guard again.`,
    },
    {
      id: "mission",
      title: "Our Mission",
      short:
        "To equip every business with an always-on AI system that watches, warns, and acts for them.",
      full: `Our mission is to deliver strategic autonomy.
      Radar+ constantly scans internal metrics and external shifts—spotting threats and unlocking opportunities in real time.
      Then it acts, auto-deploying action plans, updating stakeholders, and triggering workflows in the background.
      Our mission is simple: remove friction from success.`,
    },
    {
      id: "vision",
      title: "Our Vision",
      short:
        "A world where businesses never fear change—because they’re always ahead of it.",
      full: `We envision a business world where change isn’t feared—it’s leveraged.
      Where insights aren't delayed until meetings. Where companies don’t just track what happened—they shape what happens next.
      Our vision is to become the backbone of every modern business: an invisible engine of strategic awareness, seamless execution, and continuous improvement.
      One system, connected to everything. One source of truth, always working. That’s the Radar+ future.`,
    },
  ];

  const timeline = [
    {
      date: "May 2022",
      milestone:
        "Radar+ concept finalized. Framework built to unify business logic and AI strategy.",
    },
    {
      date: "November 2022",
      milestone:
        "First prototype launched, with auto-generated mitigation plans.",
    },
    {
      date: "March 2023",
      milestone:
        "Pilot test saved a logistics firm $82K in delay risk with early alerts.",
    },
    {
      date: "September 2023",
      milestone:
        "Added ERP integration layer and automated financial insights.",
    },
    {
      date: "January 2024",
      milestone:
        "Official launch. First 10 companies onboarded in under 3 weeks.",
    },
    {
      date: "July 2024",
      milestone:
        "Introduced real-time opportunity execution via Slack + Asana.",
    },
    {
      date: "February 2025",
      milestone:
        "Launched international capability for multi-region forecasting and automation.",
    },
  ];

  const toggleExpand = (id) => {
    setExpanded(expanded === id ? null : id);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) =>
          entry.isIntersecting ? entry.target.classList.add("fade-in") : null
        );
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll(".timeline-item");
    elements.forEach((el) => observer.observe(el));
    return () => elements.forEach((el) => observer.unobserve(el));
  }, []);

  return (
    <>
      <NavigationBar />
      <div className="about-container">
        <div className="about-intro">
          <h1>Our Purpose, Mission & Vision</h1>
          <p>
            Radar+ is more than software. It’s the operating system for
            business certainty—built to automate strategic clarity and empower
            growth.
          </p>
        </div>

        <div className="section-grid">
          {cards.map((card) => (
            <div
              className={`card ${expanded === card.id ? "expanded" : ""}`}
              key={card.id}
            >
              <h2>{card.title}</h2>
              <p>{card.short}</p>
              {expanded === card.id && (
                <div className="expanded-text">
                  <p>{card.full}</p>
                </div>
              )}
              <span
                className="toggle-link"
                onClick={() => toggleExpand(card.id)}
              >
                {expanded === card.id ? "Show less" : "Learn more →"}
              </span>
            </div>
          ))}
        </div>

        <div className="timeline-section" ref={timelineRef}>
          <h2>Milestones</h2>
          <div className="timeline-vertical">
            {timeline.map((item, index) => (
              <div className="timeline-item" key={index}>
                <div className="timeline-dot" />
                <div className="timeline-date">{item.date}</div>
                <div className="timeline-text">{item.milestone}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

export default AboutUsPage;
